/**
 * HomePage.tsx — 首页
 * 展示精选（按点赞排序）+ 最新 Prompts，支持分类筛选与关键词搜索（?q=）。
 * 数据来自 API（D1），后端不可用时由 api.ts 自动降级到种子数据。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getCategories, getPrompts } from '../lib/api';
import type { Prompt, Category } from '../data/seed';
import CategoryFilter from '../components/CategoryFilter';
import PromptCard from '../components/PromptCard';
import { Loading, EmptyState } from '../components/States';

export default function HomePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const [categories, setCategories] = useState<Category[]>([]);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<string | undefined>(
    searchParams.get('category') ?? undefined,
  );

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const [cats, list] = await Promise.all([
        getCategories(),
        getPrompts({ sort: 'latest', limit: 50 }),
      ]);
      if (cancelled) return;
      setCategories(cats);
      setPrompts(list);
      setLoading(false);
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [q]);

  const handleSelectCategory = useCallback(
    (slug: string | undefined) => {
      setActiveCategory(slug);
      const next = new URLSearchParams(searchParams.toString());
      if (slug) {
        next.set('category', slug);
      } else {
        next.delete('category');
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // 分类 + 关键词过滤
  const filtered = useMemo(() => {
    let list = prompts;
    if (activeCategory) {
      list = list.filter((p) => p.category === activeCategory);
    }
    if (q) {
      const kw = q.toLowerCase();
      list = list.filter(
        (p) => p.title.toLowerCase().includes(kw) || p.content.toLowerCase().includes(kw),
      );
    }
    return list;
  }, [prompts, activeCategory, q]);

  // 精选：按点赞数取前 6
  const featured = useMemo(
    () => [...filtered].sort((a, b) => b.likes - a.likes).slice(0, 6),
    [filtered],
  );
  // 最新：按创建时间取前 12
  const latest = useMemo(
    () => [...filtered].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, 12),
    [filtered],
  );

  return (
    <div className="space-y-10">
      {/* Hero */}
      <section className="rounded-3xl bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-600 px-6 py-12 text-center text-white shadow-lg">
        <h1 className="text-3xl font-bold sm:text-4xl">PromptHub · AI 提示词收藏站</h1>
        <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-indigo-100 sm:text-base">
          收藏、搜索、分享优质 AI Prompt，覆盖写作、编程、绘画、数据分析与办公五大场景。
          复制即用，人人可提交。
        </p>
      </section>

      {q && (
        <p className="text-sm text-slate-500">
          搜索「<span className="font-semibold text-indigo-600">{q}</span>」共
          <span className="font-semibold text-slate-800"> {filtered.length} </span>条结果
        </p>
      )}

      {/* 分类筛选 */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-slate-900">按分类浏览</h2>
        <CategoryFilter
          categories={categories}
          activeSlug={activeCategory}
          onSelect={handleSelectCategory}
        />
      </section>

      {/* 精选 */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-slate-900">精选 Prompts</h2>
        {loading ? (
          <Loading />
        ) : featured.length === 0 ? (
          <EmptyState message="没有找到匹配的 Prompt，换个关键词试试" />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {featured.map((prompt) => (
              <PromptCard key={prompt.id} prompt={prompt} />
            ))}
          </div>
        )}
      </section>

      {/* 最新 */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-slate-900">最新 Prompts</h2>
        {loading ? (
          <Loading />
        ) : latest.length === 0 ? (
          <EmptyState message="还没有内容，来提交第一条吧" />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {latest.map((prompt) => (
              <PromptCard key={prompt.id} prompt={prompt} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
